/**
 * Lodging a proxy — the GM's side of it.
 *
 * 2015-I-1 counts a proxy toward the quorum of eight, 2022-committee-quorum-voting §1 gives an
 * absent GM the proposals in advance so he can vote before the meeting, and 2019-I-2 §3 gives him
 * 24 hours on any wording changed in the room. The league has been doing all three by text
 * message. This is the same procedure with a record attached.
 *
 * THE ORDER OF THE SCREEN IS THE ORDER OF THE DECISION. Can you make it — then, if not, what do
 * you want done on each item — then hand it over. The handover is deliberately a separate act
 * from writing the instructions: until it happens nobody but the author knows the ballot exists,
 * and the panel says so on every row.
 *
 * See `domain/proxy.ts` for the state machine and for the one choice here that is NOT in the
 * rulebook — sealing.
 */

import { useMemo } from 'react';
import {
  type Mark, type ProxyBallot, canLodge, instruct, instructedCount, instructionNote, isLodged,
  lodge, sealOf, setAttending, withdraw,
} from '../domain/proxy.js';
import type { RuleProposal } from '../store/types.js';

const MARKS: ReadonlyArray<{ id: Mark; label: string }> = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
  { id: 'abstain', label: 'Abstain' },
];

export interface ProxyPanelProps {
  gm: string;
  season: number;
  agenda: readonly RuleProposal[];
  ballot: ProxyBallot | null;
  /** The item the room is on, if the meeting is running — an instruction on it is past editing. */
  openItemId?: string;
  onSave: (b: ProxyBallot) => void;
}

export function ProxyPanel({ gm, season, agenda, ballot, openItemId, onSave }: ProxyPanelProps) {
  const b: ProxyBallot = useMemo(
    () => ballot ?? { gm: gm.toUpperCase(), season },
    [ballot, gm, season],
  );
  const now = () => new Date().toISOString();
  const lodged = isLodged(b);
  const undecided = b.attending === undefined;
  const gate = canLodge(b);

  /* The card's whole posture, in one word, because three states want three different cards. */
  const state: 'undecided' | 'absent' | 'lodged' = lodged ? 'lodged'
    : b.attending === false ? 'absent' : 'undecided';

  return (
    <section className={`card proxycard ${state}`}>
      <div className="striptop">
        <h3 className="striphead">
          {state === 'lodged' ? 'Proxy lodged'
            : state === 'absent' ? 'You are marked absent'
              : 'Can you make the Competition Committee?'}
        </h3>
        {lodged && (
          <span className="mut">
            {instructedCount(b)} of {agenda.length} instructed
          </span>
        )}
      </div>

      <p className="note">
        {state === 'lodged'
          ? 'Counted toward quorum. Withdraw any time before your item is called.'
          : state === 'absent'
            ? 'Mark the items below, then lodge. Nothing is visible until you do.'
            : 'Quorum needs eight of ten — in person, on a call, or by proxy.'}
      </p>

      <div className="proxyattend">
        <button type="button" className={`btn ${b.attending === true ? 'primary' : ''}`}
          aria-pressed={b.attending === true}
          onClick={() => onSave(setAttending(b, true, now()))}>
          I’ll be there
        </button>
        <button type="button" className={`btn ${b.attending === false ? 'primary' : ''}`}
          aria-pressed={b.attending === false}
          onClick={() => onSave(setAttending(b, false, now()))}>
          I can’t make it
        </button>
      </div>

      {!undecided && (
        <>
          <ol className="proxyitems">
            {agenda.map((p, i) => {
              const mark = b.instructions?.[p.id];
              const open = openItemId === p.id;
              const seal = sealOf(b, open);
              /*
                PAST EDITING ONCE THE ROOM IS ON IT. The commissioner has already read the
                instruction and cast it; changing it now would be changing a vote after it was
                counted.
              */
              const frozen = lodged && open;
              return (
                <li key={p.id} className={frozen ? 'frozen' : ''}>
                  <span className="proxywhat">
                    <span className="proxyno">{i + 1}</span>
                    <b>{p.title}</b>
                    <span className="proxyseal mut">
                      {b.attending === false
                        ? instructionNote(seal, Boolean(mark), i + 1)
                        : 'Private mark — only you'}
                    </span>
                  </span>
                  <span className="proxymarks">
                    {MARKS.map((m) => (
                      <button key={m.id} type="button" disabled={frozen}
                        aria-pressed={mark === m.id}
                        className={`proxymark ${m.id} ${mark === m.id ? 'on' : ''}`}
                        onClick={() => onSave(instruct(b, p.id, mark === m.id ? null : m.id, now()))}>
                        {m.label}
                      </button>
                    ))}
                  </span>
                </li>
              );
            })}
          </ol>

          {/*
            THE HANDOVER IS A BUTTON ONLY WHEN THERE IS SOMETHING TO DO. A GM who is attending has
            no action here — his marks are preparation — and a primary-looking control in that
            state is a control that lies about what pressing it achieves.
          */}
          <div className={`proxyfoot ${lodged ? 'lodged' : ''}`}>
            {b.attending === false ? (
              lodged ? (
                <button type="button" className="btn danger"
                  onClick={() => onSave(withdraw(b, now()))}>
                  Withdraw proxy
                </button>
              ) : (
                <button type="button" className="btn primary" disabled={!gate.allowed}
                  title={gate.allowed ? undefined : gate.why}
                  onClick={() => onSave(lodge(b, now()))}>
                  Lodge proxy
                </button>
              )
            ) : (
              <span className="mut">Saved as you go.</span>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * WHO CAN SEE WHAT, stated rather than implied.
 *
 * Sealing is this app's own choice and not a rule (see `domain/proxy.ts`), which makes it exactly
 * the kind of promise that has to be written down where the person relying on it can read it.
 */
/** Who can see this. Three rows, because the answer changes once it is lodged. */
export function ProxyVisibility({ lodged, items }: { lodged: boolean; items: number }) {
  return (
    <section className="card proxyvis">
      <h3 className="striphead">Who can see this</h3>
      <dl>
        <div>
          <dt>You</dt>
          <dd>Everything, always.</dd>
        </div>
        <div>
          <dt>Commissioners, before</dt>
          <dd>{lodged ? `That it exists, and that it covers ${items}. Not what it says.` : 'Nothing.'}</dd>
        </div>
        <div>
          <dt>Commissioners, at the item</dt>
          <dd>{lodged ? 'One instruction, as its item is called.' : 'Nothing.'}</dd>
        </div>
      </dl>
    </section>
  );
}
